import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { SelectField } from "../components/ui/select-field.js";
import { api, getAccessToken, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { LEAD_ROLES, HR_ROLES } from "@hrms/types";
import { AsyncButton } from "../components/ui/async-button.js";
import { usePageBack } from "../context/PageBackContext.js";

function decodeToken(token: string): { role: string } {
  try {
    const p = JSON.parse(atob(token.split(".")[1]!)) as Record<string, unknown>;
    return { role: String(p["role"] ?? "") };
  } catch { return { role: "" }; }
}

interface Employee { id: string; firstName: string; middleName?: string | null; lastName: string; employeeCode: string }
interface SuccessionPlan {
  id: string; role: string; readiness: string; notes: string | null; createdAt: string;
  employee: Employee; nominee: Employee;
}
interface WorkforcePlanEntry {
  id: string; role: string; headcount: number; quarter: string; status: string; notes: string | null;
  department: { id: string; name: string } | null;
}

const inputStyle: React.CSSProperties = {
  padding: "8px 10px", border: "1px solid var(--color-surface-200)",
  borderRadius: "var(--radius-md)", fontSize: "14px", background: "var(--color-surface-0)", width: "100%",
};
const btnStyle: React.CSSProperties = {
  padding: "8px 16px", borderRadius: "var(--radius-md)", fontSize: "13px",
  fontWeight: 600, cursor: "pointer", border: "none",
  background: "var(--color-brand-600)", color: "#fff",
};

const readinessColor = (r: string): React.CSSProperties => {
  if (r === "READY_NOW")    return { background: "var(--color-success-bg)", color: "var(--color-success-fg)" };
  if (r === "READY_1_YEAR") return { background: "var(--color-warning-bg)", color: "var(--color-warning-fg)" };
  return { background: "var(--color-surface-100)", color: "var(--color-text-secondary)" };
};
const statusColor = (s: string): React.CSSProperties => {
  if (s === "FILLED")     return { background: "var(--color-success-bg)", color: "var(--color-success-fg)" };
  if (s === "CANCELLED")  return { background: "var(--color-error-bg)", color: "var(--color-error-fg)" };
  return { background: "var(--color-info-bg)", color: "var(--color-info-fg)" };
};

const TABS = ["Succession Plans", "Workforce Planning"] as const;
type Tab = typeof TABS[number];

export default function SuccessionPage() {
  usePageBack("/dashboard");
  const navigate = useNavigate();
  const accessToken = getAccessToken() ?? "";
  const { role } = decodeToken(accessToken);
  const canView = LEAD_ROLES.has(role);
  const canDelete = HR_ROLES.has(role);

  const [tab, setTab] = useState<Tab>("Succession Plans");
  const [plans, setPlans] = useState<SuccessionPlan[]>([]);
  const [wfEntries, setWfEntries] = useState<WorkforcePlanEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [showWfForm, setShowWfForm] = useState(false);
  const [planForm, setPlanForm] = useState({ employeeId: "", nomineeId: "", role: "", readiness: "NOT_READY", notes: "" });
  const [wfForm, setWfForm] = useState({ role: "", headcount: "1", quarter: "", departmentId: "", notes: "" });
  const [saving, setSaving] = useState(false);

  async function loadPlans() {
    setLoading(true);
    try {
      const r = await api.get<{ data: SuccessionPlan[] }>("/succession?limit=50");
      setPlans(r.data.data);
    } catch (err) { logError("SuccessionPage.loadPlans", err); setError("Failed to load succession plans"); }
    finally { setLoading(false); }
  }

  async function loadWf() {
    setLoading(true);
    try {
      const r = await api.get<{ data: WorkforcePlanEntry[] }>("/workforce-plans?limit=50");
      setWfEntries(r.data.data);
    } catch (err) { logError("SuccessionPage.loadWf", err); setError("Failed to load workforce plans"); }
    finally { setLoading(false); }
  }

  useEffect(() => {
    if (!canView) { setLoading(false); return; }
    if (tab === "Succession Plans") loadPlans();
    else loadWf();
  }, [tab]);

  async function createPlan() {
    setSaving(true); setError("");
    try {
      await api.post("/succession", {
        ...planForm,
        notes: planForm.notes || undefined,
      });
      setShowPlanForm(false);
      setPlanForm({ employeeId: "", nomineeId: "", role: "", readiness: "NOT_READY", notes: "" });
      loadPlans();
    } catch (e: unknown) {
      logError("SuccessionPage.createPlan", e);
      const msg = errMsg(e);
      setError(msg ?? "Failed to create plan");
      throw e;
    } finally { setSaving(false); }
  }

  async function createWf() {
    setSaving(true); setError("");
    try {
      await api.post("/workforce-plans", {
        role: wfForm.role,
        headcount: parseInt(wfForm.headcount, 10),
        quarter: wfForm.quarter,
        departmentId: wfForm.departmentId || undefined,
        notes: wfForm.notes || undefined,
      });
      setShowWfForm(false);
      setWfForm({ role: "", headcount: "1", quarter: "", departmentId: "", notes: "" });
      loadWf();
    } catch (e: unknown) {
      logError("SuccessionPage.createWf", e);
      const msg = errMsg(e);
      setError(msg ?? "Failed to create entry");
      throw e;
    } finally { setSaving(false); }
  }

  async function deletePlan(id: string) {
    try {
      await api.delete(`/succession/${id}`);
      loadPlans();
    } catch (err) { logError("SuccessionPage.deletePlan", err); setError("Failed to delete plan"); }
  }

  async function updateWfStatus(id: string, status: string) {
    try {
      await api.patch(`/workforce-plans/${id}`, { status });
      loadWf();
    } catch (err) { logError("SuccessionPage.updateWfStatus", err); setError("Failed to update status"); }
  }

  const tabBtnStyle = (active: boolean): React.CSSProperties => ({
    padding: "8px 16px", borderRadius: "var(--radius-md)", fontSize: "13px",
    fontWeight: active ? 700 : 500, cursor: "pointer",
    border: "1px solid " + (active ? "var(--color-brand-600)" : "var(--color-surface-200)"),
    background: active ? "var(--color-brand-600)" : "var(--color-surface-0)",
    color: active ? "#fff" : "var(--color-surface-700)",
  });

  if (!canView) {
    return <div style={{ padding: "32px" }}>Access denied.</div>;
  }

  return (
    <div style={{ padding: "32px", maxWidth: "1100px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
        <h1 style={{ fontSize: "22px", fontWeight: 700 }}>Succession & Workforce Planning</h1>
        <div style={{ display: "flex", gap: "8px" }}>
          {tab === "Succession Plans" && !showPlanForm && (
            <button style={btnStyle} onClick={() => setShowPlanForm(true)}>+ Add Plan</button>
          )}
          {tab === "Workforce Planning" && !showWfForm && (
            <button style={btnStyle} onClick={() => setShowWfForm(true)}>+ Add Entry</button>
          )}
        </div>
      </div>

      {error && (
        <div style={{ background: "var(--color-error-bg)", color: "var(--color-error-fg)", padding: "10px 14px", borderRadius: "var(--radius-md)", marginBottom: "16px", fontSize: "13px" }}>
          {error} <button style={{ float: "right", background: "none", border: "none", cursor: "pointer", color: "var(--color-error-fg)" }} onClick={() => setError("")}>×</button>
        </div>
      )}

      {/* Succession Plan Form */}
      {showPlanForm && (
        <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: "20px", marginBottom: "20px" }}>
          <h2 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "16px" }}>New Succession Plan</h2>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            <div>
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>Employee ID (incumbent) *</label>
              <input style={inputStyle} value={planForm.employeeId} onChange={e => setPlanForm(f => ({ ...f, employeeId: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>Nominee Employee ID *</label>
              <input style={inputStyle} value={planForm.nomineeId} onChange={e => setPlanForm(f => ({ ...f, nomineeId: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>Role / Position *</label>
              <input style={inputStyle} value={planForm.role} onChange={e => setPlanForm(f => ({ ...f, role: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>Readiness</label>
              <SelectField value={planForm.readiness} onChange={e => setPlanForm(f => ({ ...f, readiness: e.target.value }))}>
                <option value="NOT_READY">Not Ready</option>
                <option value="READY_1_YEAR">Ready in 1 Year</option>
                <option value="READY_NOW">Ready Now</option>
              </SelectField>
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>Notes</label>
              <textarea style={{ ...inputStyle, resize: "vertical", height: "60px" }} value={planForm.notes} onChange={e => setPlanForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: "flex", gap: "10px", marginTop: "16px" }}>
            <AsyncButton onClick={() => createPlan()} disabled={saving}>Create</AsyncButton>
            <button style={{ ...btnStyle, background: "var(--color-surface-200)", color: "var(--color-surface-700)" }} onClick={() => setShowPlanForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Workforce Planning Form */}
      {showWfForm && (
        <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: "20px", marginBottom: "20px" }}>
          <h2 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "16px" }}>New Workforce Plan Entry</h2>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            <div>
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>Role / Position *</label>
              <input style={inputStyle} value={wfForm.role} onChange={e => setWfForm(f => ({ ...f, role: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>Quarter * (e.g. 2026-Q3)</label>
              <input style={inputStyle} value={wfForm.quarter} onChange={e => setWfForm(f => ({ ...f, quarter: e.target.value }))} placeholder="2026-Q3" />
            </div>
            <div>
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>Headcount</label>
              <input style={inputStyle} type="number" min="1" value={wfForm.headcount} onChange={e => setWfForm(f => ({ ...f, headcount: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>Department ID</label>
              <input style={inputStyle} value={wfForm.departmentId} onChange={e => setWfForm(f => ({ ...f, departmentId: e.target.value }))} placeholder="Optional" />
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>Notes</label>
              <textarea style={{ ...inputStyle, resize: "vertical", height: "60px" }} value={wfForm.notes} onChange={e => setWfForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: "flex", gap: "10px", marginTop: "16px" }}>
            <AsyncButton onClick={() => createWf()} disabled={saving}>Create</AsyncButton>
            <button style={{ ...btnStyle, background: "var(--color-surface-200)", color: "var(--color-surface-700)" }} onClick={() => setShowWfForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "24px" }}>
        {TABS.map(t => <button key={t} style={tabBtnStyle(tab === t)} onClick={() => setTab(t as Tab)}>{t}</button>)}
      </div>

      {loading && <p>Loading…</p>}

      {/* Succession Plans table */}
      {!loading && tab === "Succession Plans" && (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr style={{ background: "var(--color-surface-50)" }}>
              {["Incumbent", "Nominee", "Role", "Readiness", "Notes", "Actions"].map(h => (
                <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600, borderBottom: "1px solid var(--color-surface-200)" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {plans.map(p => (
              <tr key={p.id} style={{ borderBottom: "1px solid var(--color-surface-100)" }}>
                <td style={{ padding: "10px 12px" }}><span style={{ cursor: "pointer" }} onClick={() => navigate(`/employees/${p.employee.id}/edit`)} onMouseEnter={e => (e.currentTarget.style.textDecoration = "underline")} onMouseLeave={e => (e.currentTarget.style.textDecoration = "none")}>{[p.employee.firstName, p.employee.middleName, p.employee.lastName].filter(Boolean).join(" ")}</span><br/><span style={{ fontSize: "11px", color: "var(--color-surface-400)" }}>{p.employee.employeeCode}</span></td>
                <td style={{ padding: "10px 12px" }}><span style={{ cursor: "pointer" }} onClick={() => navigate(`/employees/${p.nominee.id}/edit`)} onMouseEnter={e => (e.currentTarget.style.textDecoration = "underline")} onMouseLeave={e => (e.currentTarget.style.textDecoration = "none")}>{[p.nominee.firstName, p.nominee.middleName, p.nominee.lastName].filter(Boolean).join(" ")}</span><br/><span style={{ fontSize: "11px", color: "var(--color-surface-400)" }}>{p.nominee.employeeCode}</span></td>
                <td style={{ padding: "10px 12px" }}>{p.role}</td>
                <td style={{ padding: "10px 12px" }}>
                  <span style={{ ...readinessColor(p.readiness), padding: "2px 8px", borderRadius: "999px", fontSize: "12px" }}>
                    {p.readiness.replace(/_/g, " ")}
                  </span>
                </td>
                <td style={{ padding: "10px 12px", color: "var(--color-surface-500)", fontSize: "12px" }}>{p.notes ?? "—"}</td>
                <td style={{ padding: "10px 12px" }}>
                  {canDelete && (
                    <button
                      style={{ ...btnStyle, fontSize: "12px", padding: "4px 10px", background: "var(--color-error)" }}
                      onClick={() => { if (confirm("Delete this succession plan?")) deletePlan(p.id); }}
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {plans.length === 0 && (
              <tr><td colSpan={6} style={{ padding: "24px", textAlign: "center", color: "var(--color-surface-500)" }}>No succession plans.</td></tr>
            )}
          </tbody>
        </table>
      )}

      {/* Workforce Planning table */}
      {!loading && tab === "Workforce Planning" && (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr style={{ background: "var(--color-surface-50)" }}>
              {["Role", "Quarter", "Headcount", "Department", "Status", "Notes", "Actions"].map(h => (
                <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600, borderBottom: "1px solid var(--color-surface-200)" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {wfEntries.map(e => (
              <tr key={e.id} style={{ borderBottom: "1px solid var(--color-surface-100)" }}>
                <td style={{ padding: "10px 12px", fontWeight: 500 }}>{e.role}</td>
                <td style={{ padding: "10px 12px" }}>{e.quarter}</td>
                <td style={{ padding: "10px 12px" }}>{e.headcount}</td>
                <td style={{ padding: "10px 12px" }}>{e.department?.name ?? "—"}</td>
                <td style={{ padding: "10px 12px" }}>
                  <span style={{ ...statusColor(e.status), padding: "2px 8px", borderRadius: "999px", fontSize: "12px" }}>{e.status}</span>
                </td>
                <td style={{ padding: "10px 12px", color: "var(--color-surface-500)", fontSize: "12px" }}>{e.notes ?? "—"}</td>
                <td style={{ padding: "10px 12px" }}>
                  {e.status === "OPEN" && (
                    <div style={{ display: "flex", gap: "4px" }}>
                      <button style={{ ...btnStyle, fontSize: "11px", padding: "3px 8px", background: "var(--color-success)" }} onClick={() => updateWfStatus(e.id, "FILLED")}>Filled</button>
                      <button style={{ ...btnStyle, fontSize: "11px", padding: "3px 8px", background: "var(--color-text-secondary)" }} onClick={() => updateWfStatus(e.id, "CANCELLED")}>Cancel</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {wfEntries.length === 0 && (
              <tr><td colSpan={7} style={{ padding: "24px", textAlign: "center", color: "var(--color-surface-500)" }}>No workforce plan entries.</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
